// @ts-nocheck
import { useQcClassification } from "@/components/quality/qc-form/useQcClassification";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, useStore } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { trpcClient } from "@/lib/trpc";
import { AlertTriangle, Loader2, PackageCheck, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useAuthStore } from "@/stores/auth.store";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { normalizeDefectTallyItems, StageDefectSelectorTable, type DefectTallyItem } from "@/components/quality/StageDefectSelectorTable";
import { QcCategorizedMediaSection } from "@/components/quality/QcCategorizedMediaSection";
import {
  CriteriaTable,
  QcInfoField,
  QcInfoGrid,
  QcTemplateSelect,
  SamplingRow,
  VerdictBar,
  checklistItemsOf,
  normalizeChecklistItems,
  resolveFormStatus,
  useQcSamplingState,
  useQcTemplateCriteria,
  type IsoCriterionResult,
} from "@/components/quality/qc-form";
import { computeQcVerdict, summarizeQcDefects } from "@/lib/aql";

import { toVnDateString } from "@apv-erp/status";
export function inferMaterialCategoryClient(mat?: any): string {
  const text = `${mat?.category || ""} ${mat?.type || ""} ${mat?.name || ""} ${mat?.materialName || ""}`
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
  if (text.includes("mang") || text.includes("bopp") || text.includes("metalize") || text.includes("film")) {
    return "FILM";
  }
  if (text.includes("muc") || text.includes("vecni") || text.includes("uv") || text.includes("dung moi") || text.includes("ink")) {
    return "INK_CHEMICAL";
  }
  if (text.includes("keo") || text.includes("glue") || text.includes("adhesive")) {
    return "ADHESIVE";
  }
  if (text.includes("khuon") || text.includes("kem") || text.includes("ctp") || text.includes("plate") || text.includes("die")) {
    return "TOOLING";
  }
  if (text.includes("song") || text.includes("carton") || text.includes("corrugated")) {
    return "CORRUGATED";
  }
  return "PAPER";
}

interface QCMaterialFormProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit: (data: any) => Promise<void> | void;
  onCancel?: () => void;
  initialData?: any;
  isSubmitting?: boolean;
  isDrawerMode?: boolean;
  formId?: string;
}

const DEFAULT_IQC_MATERIAL_CATEGORIES = [
  { code: "PAPER", label: "Giấy cuộn / Giấy tờ (Ivory, Duplex, Couche, Kraft)" },
  { code: "FILM", label: "Màng cán (BOPP Bóng/Mờ, Metalize)" },
  { code: "INK_CHEMICAL", label: "Mực in Offset, Vecni, Dung môi" },
  { code: "ADHESIVE", label: "Keo dán hộp, Keo bồi, Keo cửa sổ" },
  { code: "TOOLING", label: "Khuôn bế, Bản kẽm CTP, Khuôn ép kim" },
  { code: "CORRUGATED", label: "Phôi sóng Carton (Sóng E, B, C, BC)" },
];

const toDateInput = (v: any) => {
  const d = v ? new Date(v) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  return toVnDateString(d);
};

/** Chỉ tiêu đã lưu trên phiếu (bản chụp riêng) — nếu chưa có thì lấy theo mẫu */
function initialChecklist(initialData: any): IsoCriterionResult[] {
  const stored = checklistItemsOf(initialData?.checklist);
  if (stored.length > 0) return stored;
  return normalizeChecklistItems(
    initialData?.template?.schema?.fields || initialData?.template?.criteria || [],
    initialData?.checklist,
    { pendingByDefault: true },
  );
}

/** Mở lại hộp thoại / đổi phiếu thì dựng lại form để lấy đúng dữ liệu ban đầu */
export function QCMaterialForm(props: QCMaterialFormProps) {
  const key = `${props.open === false ? "closed" : "open"}-${props.initialData?.id || "new"}`;
  return <QCMaterialFormInner key={key} {...props} />;
}

function QCMaterialFormInner({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  initialData,
  isSubmitting = false,
  formId = "qc-material-canonical-form",
}: QCMaterialFormProps) {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const isEditMode = Boolean(initialData?.id);

  const { data: sysConfigs } = useQuery({
    queryKey: ["configs", "QC_MATERIAL_CATEGORIES"],
    queryFn: () => trpcClient.config.getAll.query(),
    staleTime: 5 * 60 * 1000,
  });
  // Nhóm vật tư: danh mục Cấu hình > Chất lượng (dùng chung với mẫu kiểm tra QC)
  const qcClass = useQcClassification();
  const materialCategoryOptions = qcClass.materialGroups.length ? qcClass.materialGroups : DEFAULT_IQC_MATERIAL_CATEGORIES;

  const { isAdmin, isDirector, role, hasPermission } = useRoleAccess();
  const isQCManager =
    role === "QC_MANAGER" ||
    role === "QAQC_MANAGER" ||
    role === "QC" ||
    hasPermission("qc-materials", "approve") ||
    hasPermission("qc-materials", "update");

  const initialStatus = String(initialData?.status || "").toUpperCase();
  const isLocked =
    isEditMode && (initialStatus === "PASSED" || initialStatus === "FAILED") && !isAdmin && !isDirector && !isQCManager;

  const [checklistItems, setChecklistItems] = useState<IsoCriterionResult[]>(() => initialChecklist(initialData));
  const [defectDetails, setDefectDetails] = useState<DefectTallyItem[]>(() => normalizeDefectTallyItems(initialData?.defectDetails));
  const [attachments, setAttachments] = useState<any>(() => initialData?.attachments || []);
  const [override, setOverride] = useState<boolean>(initialStatus === "WARNING");
  const [overrideReason, setOverrideReason] = useState<string>(initialData?.overrideReason || "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const initialLot = Math.max(
    0,
    Math.round(Number(initialData?.quantity ?? initialData?.qtyReceived ?? initialData?.lotSize ?? 1000) || 0),
  );
  const sampling = useQcSamplingState(
    {
      lotSize: initialLot,
      mode: initialData?.inspectionMode,
      level: initialData?.aqlLevel,
      aqlMajor: initialData?.aqlMajor ?? initialData?.aqlStandard,
      aqlMinor: initialData?.aqlMinor ?? undefined,
      sampleSize: initialData?.sampleSize ?? undefined,
      acMajor: initialData?.acMajor ?? undefined,
      reMajor: initialData?.reMajor ?? undefined,
      acMinor: initialData?.acMinor ?? null,
      reMinor: initialData?.reMinor ?? null,
    },
    { recomputeOnMount: !isEditMode },
  );

  const initialDate = toDateInput(initialData?.checkTime);
  const form = useForm({
    defaultValues: {
      materialId: initialData?.materialId || initialData?.item_id || "",
      vendorId: initialData?.vendorId || initialData?.material?.vendorId || "",
      purchaseOrderId: initialData?.purchaseOrderId || "",
      receiptDocId: initialData?.receiptDocId || "",
      templateId: initialData?.templateId || "",
      inspectorId: initialData?.inspectorId || currentUser?.id || "",
      batchNumber:
        initialData?.batchNumber || initialData?.batch_no || `LOT-${toVnDateString().replace(/-/g, "")}`,
      coaNumber: initialData?.coaNumber || "",
      coaVerified: initialData?.coaVerified !== undefined ? Boolean(initialData.coaVerified) : true,
      materialCategory: initialData?.materialCategory || inferMaterialCategoryClient(initialData?.material || initialData),
      rejectedQty: Number(initialData?.rejectedQty ?? initialData?.qtyRejected ?? 0) || 0,
      unit: initialData?.unit || initialData?.material?.unit || "Tờ",
      checkDate: initialDate,
      notes: initialData?.notes || "",
    },
    onSubmit: async ({ value }) => {
      const nextErrors: Record<string, string> = {};
      if (!value.materialId) nextErrors.materialId = "Chọn vật tư";
      if (!String(value.batchNumber || "").trim()) nextErrors.batchNumber = "Nhập số lô NCC";
      if (!value.inspectorId) nextErrors.inspectorId = "Chọn người kiểm";
      if (!(sampling.lotSize > 0)) nextErrors.lotSize = "Cỡ lô phải lớn hơn 0";
      const counts = summarizeQcDefects(defectDetails);
      const verdict = computeQcVerdict({ counts, reMajor: sampling.reMajor, reMinor: sampling.reMinor, criteria: checklistItems });
      const status = resolveFormStatus(verdict.verdict, override);
      if (status === "WARNING" && !overrideReason.trim()) nextErrors.overrideReason = "Nhân nhượng phải ghi lý do";
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      const lot = sampling.lotSize;
      const rejected = Math.min(lot, Math.max(0, Number(value.rejectedQty) || 0));
      const accepted = Math.max(0, lot - rejected);
      const ng = counts.total;
      const prevTp = initialData?.technicalParams && typeof initialData.technicalParams === "object" ? initialData.technicalParams : {};

      await onSubmit({
        materialId: value.materialId,
        materialCategory: value.materialCategory,
        vendorId: value.vendorId || null,
        purchaseOrderId: value.purchaseOrderId || null,
        receiptDocId: value.receiptDocId || null,
        templateId: value.templateId || null,
        inspectorId: value.inspectorId,
        batchNumber: value.batchNumber,
        coaVerified: Boolean(value.coaVerified),
        checkTime:
          value.checkDate && value.checkDate !== initialDate
            ? new Date(`${value.checkDate}T${new Date().toTimeString().slice(0, 8)}`)
            : initialData?.checkTime || undefined,
        status,
        overrideReason: status === "WARNING" ? overrideReason.trim() : null,
        inspectionMode: sampling.mode,
        qtyReceived: lot,
        lotSize: lot,
        qtyInspected: sampling.sampleSize,
        sampleSize: sampling.sampleSize,
        qtyAccepted: accepted,
        qtyRejected: rejected,
        okSampleQty: Math.max(0, sampling.sampleSize - ng),
        ngSampleQty: ng,
        aqlLevel: sampling.level,
        aqlMajor: sampling.aqlMajor,
        aqlMinor: sampling.aqlMinor,
        acMajor: sampling.acMajor,
        reMajor: sampling.reMajor,
        acMinor: sampling.acMinor,
        reMinor: sampling.reMinor,
        criticalDefects: counts.critical,
        majorDefects: counts.major,
        minorDefects: counts.minor,
        grainDirection: initialData?.grainDirection ?? null,
        checklist: checklistItems,
        defectDetails,
        attachments,
        notes: value.notes || null,
        technicalParams: {
          ...prevTp,
          unit: value.unit || null,
          invoiceNumber: prevTp.invoiceNumber ?? initialData?.invoiceNumber ?? null,
          coaNumber: value.coaNumber || null,
        },
      });
    },
  });

  const materialCategory = useStore(form.store, (st: any) => st.values.materialCategory);
  const templateId = useStore(form.store, (st: any) => st.values.templateId);
  const rejectedQty = useStore(form.store, (st: any) => st.values.rejectedQty);
  const unit = useStore(form.store, (st: any) => st.values.unit);

  const { templates, selectTemplate } = useQcTemplateCriteria({
    qcType: "IQC",
    materialCategory,
    templateId: templateId || null,
    checklistItems,
    onTemplateChange: (id) => form.setFieldValue("templateId", id || ""),
    onChecklistChange: setChecklistItems,
    normalizeOptions: { pendingByDefault: true },
    disabled: isLocked,
  });

  useEffect(() => {
    if (initialData?.id) setChecklistItems(initialChecklist(initialData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id]);

  const counts = useMemo(() => summarizeQcDefects(defectDetails), [defectDetails]);
  const acceptedQty = Math.max(0, sampling.lotSize - Math.min(sampling.lotSize, Number(rejectedQty) || 0));

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const resetCriteria = () => {
    form.setFieldValue("templateId", "");
    setChecklistItems([]);
  };

  const formContent = (
    <form id={formId} onSubmit={handleFormSubmit} className="space-y-4">
      {isLocked && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs font-medium text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          Phiếu đã chốt kết quả. Chỉ Quản lý QC hoặc Ban Giám Đốc được sửa.
        </div>
      )}

      <fieldset disabled={isLocked} className="min-w-0 space-y-4">
        {/* 1. Thông tin chung + Lấy mẫu */}
        <div className="space-y-3.5 rounded-xl border border-slate-200 bg-white p-4 shadow-2xs">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
            <PackageCheck className="h-4 w-4 text-indigo-600" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">Thông tin chung</h3>
          </div>

          <QcInfoGrid>
            <QcInfoField label="Vật tư" required span={2} error={errors.materialId}>
              <form.Field
                name="materialId"
                children={(field) => (
                  <SharedCombobox
                    type="material"
                    value={field.state.value || ""}
                    onChange={(id, opt) => {
                      field.handleChange(id);
                      const orig = opt?.original || opt?.record;
                      if (orig) {
                        if (orig.unit) form.setFieldValue("unit", orig.unit);
                        // NCC mặc định của vật tư chỉ điền khi ô NCC trống — không ghi đè NCC của đơn mua
                        if (orig.vendorId && !form.getFieldValue("vendorId")) form.setFieldValue("vendorId", orig.vendorId);
                        form.setFieldValue("materialCategory", inferMaterialCategoryClient(orig));
                        resetCriteria();
                      }
                    }}
                    placeholder="Chọn vật tư..."
                  />
                )}
              />
            </QcInfoField>
            <QcInfoField label="Nhóm vật tư" htmlFor="iqc-material-category">
              <form.Field
                name="materialCategory"
                children={(field) => (
                  <Select
                    value={field.state.value || "PAPER"}
                    onValueChange={(val) => {
                      field.handleChange(val);
                      resetCriteria();
                    }}
                  >
                    <SelectTrigger id="iqc-material-category" className="h-9 w-full min-w-0 text-xs font-semibold *:data-[slot=select-value]:truncate">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {materialCategoryOptions.map((cat: any) => (
                        <SelectItem key={cat.code} value={cat.code}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </QcInfoField>
            <QcInfoField label="Nhà cung cấp">
              <form.Field
                name="vendorId"
                children={(field) => (
                  <SharedCombobox
                    type="vendor"
                    value={field.state.value || ""}
                    onChange={(id) => field.handleChange(id)}
                    placeholder="Chọn nhà cung cấp..."
                  />
                )}
              />
            </QcInfoField>
            <QcInfoField label="Đơn mua">
              <form.Field
                name="purchaseOrderId"
                children={(field) => (
                  <SharedCombobox
                    type="purchaseOrder"
                    value={field.state.value || ""}
                    onChange={(id, opt) => {
                      field.handleChange(id);
                      const orig = opt?.original || opt?.record;
                      if (orig?.vendorId) form.setFieldValue("vendorId", orig.vendorId);
                      if (orig?.items?.length > 0 && !form.getFieldValue("materialId")) {
                        const firstItem = orig.items[0];
                        if (firstItem.materialId) form.setFieldValue("materialId", firstItem.materialId);
                        if (firstItem.quantity) sampling.setLotSize(Number(firstItem.quantity));
                      }
                    }}
                    placeholder="Chọn đơn mua..."
                  />
                )}
              />
            </QcInfoField>
            <QcInfoField label="Số lô NCC" required htmlFor="iqc-batch" error={errors.batchNumber}>
              <form.Field
                name="batchNumber"
                children={(field) => (
                  <Input
                    id="iqc-batch"
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="VD: LOT-202610-01"
                    className="h-9 text-xs font-mono font-bold"
                  />
                )}
              />
            </QcInfoField>
            <QcInfoField label="Số CoA" htmlFor="iqc-coa">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <form.Field
                  name="coaNumber"
                  children={(field) => (
                    <Input
                      id="iqc-coa"
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="VD: COA-8821"
                      className="h-9 min-w-[7rem] flex-1 text-xs font-mono"
                    />
                  )}
                />
                <form.Field
                  name="coaVerified"
                  children={(field) => (
                    <label className="flex shrink-0 cursor-pointer items-center gap-1 text-[11px] text-slate-600" title="Đã đối chiếu CoA của NCC">
                      <input
                        type="checkbox"
                        checked={Boolean(field.state.value)}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-slate-300"
                      />
                      Đã đối chiếu
                    </label>
                  )}
                />
              </div>
            </QcInfoField>
            <QcInfoField label="Ngày kiểm" htmlFor="iqc-check-date">
              <form.Field
                name="checkDate"
                children={(field) => (
                  <Input
                    id="iqc-check-date"
                    type="date"
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className="h-9 text-xs"
                  />
                )}
              />
            </QcInfoField>
            <QcInfoField label="Người kiểm" required error={errors.inspectorId}>
              <form.Field
                name="inspectorId"
                children={(field) => (
                  <SharedCombobox
                    type="user"
                    params={{ processGroup: 'QC' }} // nhân sự theo quy trình (03/11/2026)
                    value={field.state.value || ""}
                    onChange={(id) => field.handleChange(id)}
                    placeholder="Chọn người kiểm..."
                  />
                )}
              />
            </QcInfoField>
          </QcInfoGrid>

          <div className="border-t border-slate-100 pt-3">
            <SamplingRow
              sampling={sampling}
              idPrefix="iqc"
              unit={unit}
              onUnitChange={(v) => form.setFieldValue("unit", v)}
              onLotSizeChange={(v) => {
                const rej = Number(form.getFieldValue("rejectedQty")) || 0;
                if (rej > v) form.setFieldValue("rejectedQty", v);
              }}
            />
            {errors.lotSize ? <p className="mt-1 text-[11px] font-medium text-destructive">{errors.lotSize}</p> : null}
          </div>
        </div>

        {/* 2. Chỉ tiêu kiểm tra */}
        <CriteriaTable
          items={checklistItems}
          onChange={isLocked ? undefined : setChecklistItems}
          toolbar={<QcTemplateSelect templates={templates} value={templateId} onChange={selectTemplate} disabled={isLocked} />}
        />

        {/* 3. Lỗi phát hiện — nguồn duy nhất của số lỗi theo mức */}
        <StageDefectSelectorTable
          processStage="IQC"
          lotSize={sampling.lotSize}
          sampleSize={sampling.sampleSize}
          defectDetails={defectDetails}
          onChange={(items) => setDefectDetails(items)}
          readOnly={isLocked}
          hideSampleSummary
        />

        {/* 4. Kết luận */}
        <VerdictBar
          idPrefix="iqc"
          counts={counts}
          sampleSize={sampling.sampleSize}
          reMajor={sampling.reMajor}
          reMinor={sampling.reMinor}
          criteria={checklistItems}
          override={override}
          onOverrideChange={isLocked ? undefined : setOverride}
          overrideReason={overrideReason}
          onOverrideReasonChange={setOverrideReason}
          reasonError={errors.overrideReason}
          readOnly={isLocked}
        >
          <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 md:grid-cols-4">
            <div className="min-w-0 space-y-1">
              <Label htmlFor="iqc-accepted" className="text-xs font-semibold text-emerald-700">
                SL nhập kho
              </Label>
              <Input
                id="iqc-accepted"
                readOnly
                tabIndex={-1}
                value={acceptedQty}
                className="h-9 cursor-default border-emerald-200 bg-emerald-50/40 text-xs font-mono font-bold text-emerald-800"
              />
            </div>
            <div className="min-w-0 space-y-1">
              <Label htmlFor="iqc-rejected" className="text-xs font-semibold text-rose-700">
                SL loại
              </Label>
              <form.Field
                name="rejectedQty"
                children={(field) => (
                  <Input
                    id="iqc-rejected"
                    type="number"
                    min={0}
                    step="any"
                    value={field.state.value ?? 0}
                    onChange={(e) => field.handleChange(Math.min(sampling.lotSize, Math.max(0, Number(e.target.value) || 0)))}
                    className="h-9 text-xs font-mono font-bold border-rose-200 bg-rose-50/30 text-rose-800"
                  />
                )}
              />
            </div>
            <div className="col-span-2 min-w-0 space-y-1">
              <Label htmlFor="iqc-notes" className="text-xs font-semibold text-slate-700">
                Nhận xét
              </Label>
              <form.Field
                name="notes"
                children={(field) => (
                  <Textarea
                    id="iqc-notes"
                    rows={2}
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Tình trạng kiện hàng, pallet, điều kiện nhân nhượng..."
                    className="min-h-9 text-xs"
                  />
                )}
              />
            </div>
          </div>
        </VerdictBar>

        <details className="rounded-xl border border-slate-200 bg-white shadow-2xs">
          <summary className="cursor-pointer px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-700">
            Ảnh &amp; tệp ({Array.isArray(attachments) ? attachments.length : 0})
          </summary>
          <div className="px-2 pb-2">
            <QcCategorizedMediaSection mode="QC" value={attachments} onChange={setAttachments} />
          </div>
        </details>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (onCancel) onCancel();
              else if (onOpenChange) onOpenChange(false);
              else navigate({ to: "/qc-materials" });
            }}
          >
            Hủy
          </Button>
          {!isLocked && (
            <form.Subscribe selector={(state) => [state.isSubmitting]}>
              {([submitting]) => (
                <Button
                  type="submit"
                  size="sm"
                  disabled={isSubmitting || submitting}
                  className="bg-indigo-600 px-5 font-semibold text-white hover:bg-indigo-700"
                >
                  {isSubmitting || submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                  {isEditMode ? "Cập Nhật Phiếu IQC" : "Lưu Biên Bản IQC"}
                </Button>
              )}
            </form.Subscribe>
          )}
        </div>
      </fieldset>
    </form>
  );

  if (typeof open === "boolean") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[95vh] max-w-[95vw] flex-col overflow-hidden bg-slate-50 p-0 lg:max-w-[1200px]">
          <DialogHeader className="sticky top-0 z-10">
            <DialogTitle>
              {isEditMode ? "Cập nhật phiếu kiểm tra vật tư đầu vào" : "Lập phiếu kiểm tra vật tư đầu vào"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Lấy mẫu theo ISO 2859-1</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">{formContent}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return formContent;
}
